// Route the idempotent cart fetches (section re-render, /cart/change.js quantity,
// /cart/update.js note) through the resilience wrapper so transient iOS
// Facebook/Instagram in-app-webview aborts retry instead of dead-ending the cart.
// Falls back to a plain fetch if cart-resilience.js didn't load. Same
// Promise<Response> contract, so the existing .then/.catch below stay untouched.
function nttCartFetch(url, options) {
  return window.CartResilience && window.CartResilience.cartFetch
    ? window.CartResilience.cartFetch(url, options)
    : fetch(url, options);
}

// #143 — target a cart mutation by the STABLE line key, not the Dawn-style line
// index. `/cart/change` renumbers line indexes on every background cart mutation
// (checkout reconcile, entitlement belt, Intelligems, ship-week attribute
// writes), so an index captured at render time goes stale between paint and
// click and Shopify answers "line parameter is invalid." The key is per-line
// identity and survives renumbering. Falls back to the index only when the
// rendered markup predates this change and carries no key.
function cartChangeTarget(line, key) {
  return typeof key === 'string' && key.length > 0 ? { id: key } : { line };
}

// The post-mutation quantity readback has the same staleness problem: reading
// parsedState.items[line - 1] lands on a DIFFERENT line if the server renumbered.
// Look the line up by key when we have one.
function findUpdatedLine(items, line, key) {
  if (!Array.isArray(items)) return undefined;
  if (typeof key === 'string' && key.length > 0) {
    return items.find((item) => item && item.key === key);
  }
  return items[line - 1];
}

// Dawn resolves its scaffolding elements by document id with a `||` fallback:
// `getElementById('main-cart-items') || getElementById('CartDrawer-CartItems')`.
// layout/theme.liquid renders the cart drawer on EVERY page, /cart included, so
// on /cart both ids exist and the `||` always short-circuits to the cart PAGE
// element. The drawer then never receives `cart__items--disabled`
// (pointer-events: none) while a mutation is in flight — its remove buttons stay
// live and a shopper can fire overlapping /cart/change calls — and its error text
// is written to #cart-errors behind the drawer, where nobody can see it.
// Resolve against the instance's own surface instead, keeping the other id as a
// fallback for surfaces that render only one of the two.
function cartScopedElement(instance, drawerId, pageId) {
  const isDrawer = instance.tagName === 'CART-DRAWER-ITEMS';
  return (
    document.getElementById(isDrawer ? drawerId : pageId) || document.getElementById(isDrawer ? pageId : drawerId)
  );
}

// A cart mutation only carries a usable render when the body has BOTH the
// re-rendered `sections` and a numeric `item_count`. Shopify answers some
// mutations with an error object instead (422 `line parameter is invalid.`, a
// rate-limit / bot-challenge body, a section render that came back empty). Dawn
// walks straight into those: `parsedState.errors` is undefined so it does not
// bail, `item_count === 0` is false for `undefined` so `is-empty` gets torn off
// <cart-drawer>, and the very next statement throws on `parsedState.sections[…]`
// before anything re-renders. That leaves the drawer holding empty-cart markup
// with no `is-empty` class, which component-cart-drawer.css paints as a blank
// panel with the "Your cart" header pushed to the bottom.
function isRenderableCartState(parsedState) {
  return !!parsedState && !!parsedState.sections && typeof parsedState.item_count === 'number';
}

// INV-035 left one thing unresolved: WHAT upstream event produces a body with no
// usable render. The 422 line-index route is reproducible in isolation but is not
// what the deployed theme posts (it sends `id: <line key>` since #143), and a
// rate-limit body is not JSON so it throws before ever reaching here. Without a
// signal the next occurrence teaches us nothing, so record the SHAPE of whatever
// came back.
//
// Shape only — never the body. A cart payload can carry `note` and `attributes`,
// which are shopper-authored, so this sends the key NAMES plus Shopify's own
// status/message strings and nothing else.
//
// Not sendBeacon: unlike NttUtils.fetchWithRetry's give-up telemetry, this fires
// on a path where the page survives (the drawer re-syncs and the shopper carries
// on), so the default async transport is correct.
function reportCartRenderFailure(reason, details) {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(
        'cart_render_unavailable',
        Object.assign(
          {
            reason,
            path: (window.location && window.location.pathname) || '',
          },
          details || {}
        )
      );
    }
  } catch (telemetryError) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('cart_render_unavailable capture skipped', telemetryError);
    }
  }
}

// The discriminator INV-035 actually needs: did this mutation post the stable
// line key, or did it fall back to the Dawn line index? If the failures cluster
// on `line`, the trigger is markup rendered without a `data-key`; if they cluster
// on `key`, it is something else entirely.
function cartTargetKind(key) {
  return typeof key === 'string' && key.length > 0 ? 'key' : 'line';
}

// Describe an unrenderable body without quoting it.
function describeCartState(parsedState) {
  if (!parsedState || typeof parsedState !== 'object') {
    return { body_type: typeof parsedState, body_keys: '' };
  }

  return {
    body_type: 'object',
    // Sorted so the same shape always produces the same string and the events
    // group cleanly in PostHog.
    body_keys: Object.keys(parsedState).sort().join(','),
    has_sections: !!parsedState.sections,
    item_count_type: typeof parsedState.item_count,
    // Shopify's own error vocabulary, e.g. "unprocessable_entity" /
    // "line parameter is invalid."
    status: typeof parsedState.status === 'undefined' ? '' : String(parsedState.status),
    message: typeof parsedState.message === 'undefined' ? '' : String(parsedState.message),
  };
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, undefined, undefined, this.dataset.key);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = cartScopedElement(this, 'CartDrawer-LineItemStatus', 'shopping-cart-line-item-status');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      event.target.dataset.key
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      nttCartFetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      nttCartFetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, key) {
    this.enableLoading(line);

    const body = JSON.stringify({
      ...cartChangeTarget(line, key),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    // Returned so callers (and tests) can await the mutation settling.
    return nttCartFetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Bail BEFORE touching any class: everything below assumes a full cart
        // payload, and half-applying it is what blanks the drawer.
        if (!isRenderableCartState(parsedState)) {
          this.handleUnrenderableCartState(parsedState, { quantity, key });
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // The other way INV-035's re-render can die: `sections` is present, so the
        // guard above passes, but one section's HTML came back empty or without
        // the selector. Dawn dereferenced that null and threw mid-loop, leaving
        // the surface half-rendered with no signal. Collect the failure instead
        // and recover the same way.
        let unrenderedSection = null;

        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;

          const innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          if (innerHTML === null) {
            unrenderedSection = unrenderedSection || section.section;
            return;
          }

          (container.querySelector(section.selector) || container).innerHTML = innerHTML;
        });

        if (unrenderedSection) {
          this.handleEmptySectionRender(unrenderedSection, { quantity, key });
          return;
        }
        const updatedLine = findUpdatedLine(parsedState.items, line, key);
        const updatedValue = updatedLine ? updatedLine.quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = cartScopedElement(this, 'CartDrawer-CartErrors', 'cart-errors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  // The mutation came back without a usable render. Show the shopper an error on
  // THEIR surface, then re-sync the markup from the server so they are not left
  // looking at a cart that disagrees with the one Shopify holds.
  handleUnrenderableCartState(parsedState, context) {
    reportCartRenderFailure(
      'no_sections',
      Object.assign(
        {
          surface: this.tagName === 'CART-DRAWER-ITEMS' ? 'drawer' : 'cart-page',
          targeted_by: cartTargetKind(context && context.key),
          quantity: context && typeof context.quantity !== 'undefined' ? Number(context.quantity) : null,
        },
        describeCartState(parsedState)
      )
    );

    this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

    return this.recoverFromRenderFailure((parsedState && parsedState.description) || window.cartStrings.error);
  }

  // `sections` was present but one of them rendered to nothing usable.
  handleEmptySectionRender(sectionName, context) {
    reportCartRenderFailure('empty_section_html', {
      surface: this.tagName === 'CART-DRAWER-ITEMS' ? 'drawer' : 'cart-page',
      targeted_by: cartTargetKind(context && context.key),
      quantity: context && typeof context.quantity !== 'undefined' ? Number(context.quantity) : null,
      section: String(sectionName),
    });

    this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

    return this.recoverFromRenderFailure(window.cartStrings.error);
  }

  // Re-sync first, then write the message: the re-sync replaces this surface's
  // markup wholesale, so a message written beforehand would be thrown away with
  // the element that held it.
  recoverFromRenderFailure(message) {
    return this.resyncFromServer().then(() => {
      const errors = cartScopedElement(this, 'CartDrawer-CartErrors', 'cart-errors');
      if (errors) errors.textContent = message;
    });
  }

  // Re-render this surface from a fresh section request. `is-empty` is what
  // decides whether the empty state or the line items are visible, so it is
  // re-derived from the markup just installed rather than left over from the
  // mutation that failed.
  resyncFromServer() {
    const isDrawer = this.tagName === 'CART-DRAWER-ITEMS';

    return nttCartFetch(`${routes.cart_url}?section_id=${isDrawer ? 'cart-drawer' : 'main-cart-items'}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (!isDrawer) {
          const source = html.querySelector('cart-items');
          if (source) this.innerHTML = source.innerHTML;
          return;
        }

        const source = html.querySelector('.drawer__inner');
        const target = document.querySelector('.drawer__inner');
        if (!source || !target) return;

        target.innerHTML = source.innerHTML;

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', !!target.querySelector('.drawer__inner-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = cartScopedElement(this, 'CartDrawer-LiveRegionText', 'cart-live-region-text');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // Returns null rather than throwing when the section HTML is empty or does not
  // contain the selector, so the caller can recover and report instead of dying
  // part-way through the render loop.
  getSectionInnerHTML(html, selector) {
    if (typeof html !== 'string' || html.length === 0) return null;

    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems = cartScopedElement(this, 'CartDrawer-CartItems', 'main-cart-items');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = cartScopedElement(this, 'CartDrawer-CartItems', 'main-cart-items');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            nttCartFetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Node-only test export. Skipped under <script> in the theme (process undefined).
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = { cartChangeTarget, findUpdatedLine, isRenderableCartState, cartTargetKind, describeCartState };
}
